import type { JSX } from 'react';

import { cn } from '../utils/cn';

export type LogoFaceVariant = 'attentive' | 'idle' | 'surprised' | 'thinking';

interface LogoFaceProps {
	variant?: LogoFaceVariant;
	className?: string;
}

function AttentivePaths() {
	return (
		<g clipPath="url(#clip-attentive)">
			<path
				d="M11.7043 4.28925L11.4078 3.91813C8.95177 0.843699 4.4684 0.342424 1.39397 2.7985L1.02285 3.09498C0.203006 3.74993 0.0693326 4.94549 0.724287 5.76534C1.37924 6.58519 2.5748 6.71886 3.39465 6.06391L3.76577 5.76744C5.2005 4.62127 7.29274 4.85519 8.43891 6.28993L8.73539 6.66105C9.39034 7.4809 10.5859 7.61457 11.4058 6.95961C12.2256 6.30466 12.3593 5.1091 11.7043 4.28925Z"
				fill="currentColor"
			/>
			<path
				d="M4.00049 8.74365C4.00049 7.91523 4.67206 7.24365 5.50049 7.24365H6.50049C7.32892 7.24365 8.00049 7.91523 8.00049 8.74365V9.74365C8.00049 10.5721 7.32892 11.2437 6.50049 11.2437H5.50049C4.67206 11.2437 4.00049 10.5721 4.00049 9.74365V8.74365Z"
				fill="currentColor"
			/>
			<defs>
				<clipPath id="clip-attentive">
					<rect width="12" height="12" fill="white" />
				</clipPath>
			</defs>
		</g>
	);
}

function IdlePaths() {
	return (
		<>
			<path
				d="M11 9V8.5C11 4.35786 7.64214 1 3.5 1H3C1.89543 1 1 1.89543 1 3C1 4.10457 1.89543 5 3 5H3.5C5.433 5 7 6.567 7 8.5V9C7 10.1046 7.89543 11 9 11C10.1046 11 11 10.1046 11 9Z"
				fill="currentColor"
			/>
			<path
				d="M1 8.5C1 7.67157 1.67157 7 2.5 7H3.5C4.32843 7 5 7.67157 5 8.5V9.5C5 10.3284 4.32843 11 3.5 11H2.5C1.67157 11 1 10.3284 1 9.5V8.5Z"
				fill="currentColor"
			/>
		</>
	);
}

function SurprisedPaths() {
	return (
		<g clipPath="url(#clip-surprised)">
			<path
				d="M11.49 3.33228L11.1936 2.96116C8.73747 -0.113271 4.25411 -0.614547 1.17968 1.84153L0.80856 2.13801C-0.0112884 2.79296 -0.144962 3.98852 0.509992 4.80837C1.16495 5.62822 2.36051 5.76189 3.18036 5.10694L3.55148 4.81047C4.98621 3.6643 7.07845 3.89822 8.22462 5.33296L8.52109 5.70408C9.17605 6.52393 10.3716 6.6576 11.1915 6.00264C12.0113 5.34769 12.145 4.15213 11.49 3.33228Z"
				fill="currentColor"
			/>
			<path
				d="M4 7.5C4 6.67157 4.67157 6 5.5 6H6.5C7.32843 6 8 6.67157 8 7.5V10.5C8 11.3284 7.32843 12 6.5 12H5.5C4.67157 12 4 11.3284 4 10.5V7.5Z"
				fill="currentColor"
			/>
			<defs>
				<clipPath id="clip-surprised">
					<rect width="12" height="12" fill="white" />
				</clipPath>
			</defs>
		</g>
	);
}

function ThinkingPaths() {
	return (
		<g clipPath="url(#clip-thinking)">
			<path
				d="M1.67887 3.92395C0.660386 4.17642 0.0390193 5.20721 0.291438 6.2257C0.543904 7.24419 1.5747 7.86556 2.59318 7.61314L3.54512 7.37632C5.23543 6.95734 7.02073 7.17273 8.56361 7.98034L9.26578 8.34796C10.1954 8.83459 11.3442 8.47527 11.8308 7.5456C12.3175 6.61593 11.9581 5.46717 11.0285 4.98054L10.3256 4.61349C7.95984 3.3752 5.22246 3.04598 2.63066 3.68844L1.67887 3.92395Z"
				fill="currentColor"
			/>
			<path
				d="M6 10.5625C6 10.0102 6.44772 9.5625 7 9.5625H9C9.55228 9.5625 10 10.0102 10 10.5625C10 11.1148 9.55228 11.5625 9 11.5625H7C6.44772 11.5625 6 11.1148 6 10.5625Z"
				fill="currentColor"
			/>
			<defs>
				<clipPath id="clip-thinking">
					<rect width="12" height="12" fill="white" />
				</clipPath>
			</defs>
		</g>
	);
}

const variantMap: Record<LogoFaceVariant, () => JSX.Element> = {
	attentive: AttentivePaths,
	idle: IdlePaths,
	surprised: SurprisedPaths,
	thinking: ThinkingPaths,
};

export function LogoFace({ variant = 'idle', className }: LogoFaceProps) {
	const Paths = variantMap[variant];

	return (
		<svg
			viewBox="0 0 12 12"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={cn('size-3', className)}
		>
			<Paths />
		</svg>
	);
}
